import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { faUser, faEnvelope, faLock, faArrowRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup } from "firebase/auth";
import "./SignupPage.css";
import { useAuth } from '../auth';

const SignupPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, signUp } = useAuth();

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      // Check if user has selected a team
      if (!user.firestoreUser?.teamSelected) {
        navigate("/team-selection", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, navigate]);

  // Handle Email/Password Signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signUp(email, password, name);
      // Redirect will be handled by useEffect based on teamSelected status
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Signup
  const handleGoogleSignup = async () => {
    setError("");
    setLoading(true);
    
    try {
      await signInWithPopup(auth, googleProvider);
      // Redirect will be handled by useEffect based on teamSelected status
    } catch (err) {
      console.error("Google signup error:", err);
      setError("Failed to sign up with Google");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Video Background */}
      <video className="auth-video-bg" autoPlay loop muted playsInline>
        <source src="/videos/f1-race.mp4" type="video/mp4" />
      </video>
      <div className="auth-video-overlay"></div>

      {/* Back to Home Button */}
      <button className="auth-back-btn" onClick={() => navigate('/')}>
        ← Back to Home
      </button>

      {/* Main Container */}
      <div className="auth-container">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <div className="auth-brand-content">
            <div className="brand-logo">
              <span className="brand-icon">🏎️</span>
              <span className="brand-text">F1 Fantasy</span>
            </div>
            
            <h1 className="brand-title">
              Join The Ultimate
              <br />
              <span className="gradient-text">F1 Experience</span>
            </h1>
            
            <p className="brand-subtitle">
              Create your dream team, compete with friends, and dominate the leaderboard.
            </p>

            <div className="brand-features">
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Build Your Dream Team</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Join Private Leagues</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>Predict Race Results</span>
              </div>
              <div className="brand-feature">
                <FontAwesomeIcon icon={faCheckCircle} className="feature-icon" />
                <span>100% Free Forever</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="auth-form-side">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2 className="form-title">Create Account</h2>
              <p className="form-subtitle">Start your F1 fantasy journey today</p>
            </div>

            {error && (
              <div className="error-banner">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* Google Signup Button */}
            <button 
              className="auth-google-btn"
              onClick={handleGoogleSignup}
              disabled={loading}
            >
              <FontAwesomeIcon icon={faGoogle} className="google-icon" />
              <span>Continue with Google</span>
            </button>

            <div className="auth-divider">
              <span className="divider-line"></span>
              <span className="divider-text">or sign up with email</span>
              <span className="divider-line"></span>
            </div>

            {/* Signup Form */}
            <form className="auth-form" onSubmit={handleSignup}>
              <div className="form-field">
                <label className="form-label">Full Name</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faUser} className="input-icon" />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Email Address</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">Password</label>
                <div className="input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="input-icon" />
                  <input
                    type="password"
                    className="form-input"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    disabled={loading}
                  />
                </div>
                <p className="field-hint">Must be at least 6 characters</p>
              </div>

              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={loading}
              >
                <span>{loading ? 'Creating Account...' : 'Create Account'}</span>
                {!loading && <FontAwesomeIcon icon={faArrowRight} className="btn-arrow" />}
              </button>
            </form>

            <p className="auth-switch-text">
              Already have an account?{" "}
              <button 
                className="auth-switch-link"
                onClick={() => navigate('/login')}
              >
                Sign in
              </button>
            </p>

            <p className="auth-terms">
              By signing up, you agree to our{" "}
              <a href="#terms" className="terms-link">Terms of Service</a>
              {" "}and{" "}
              <a href="#privacy" className="terms-link">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
